import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { Filter, Search, X, Calendar, ChevronDown } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { getUserLabels } from "../services/labelService";

const AUTHORITY_DEPARTMENTS = [
  "Residential",
  "Institutional",
  "IT",
  "HRD",
  "Water",
  "Urban Services",
  "Land",
  "Builders",
  "Group Housing",
  "Sewar",
  "Health",
  "University",
  "Finance",
  "Commercial",
  "6% Abadi Plot",
  "Others",
];

/**
 * Reusable search + filter bar, mirroring the All Cases page exactly.
 *
 * Props:
 *  - cases: the full list of cases for the current page (array)
 *  - onFilteredChange: called with the filtered list whenever it changes
 *  - className: optional wrapper class overrides (width etc.)
 */
export default function CaseSearchFilter({ cases = [], onFilteredChange, className = "" }) {
  const { globalUser } = useAuth();

  const [labels, setLabels] = useState([]);
  const [isDepartmentVerified, setIsDepartmentVerified] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [showFilterOptions, setShowFilterOptions] = useState(false);
  const [searchField, setSearchField] = useState("all");
  const [caseYearFilter, setCaseYearFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [labelFilter, setLabelFilter] = useState("all");
  const [hearingDateFilter, setHearingDateFilter] = useState("");
  const [disposedWithDirectionFilter, setDisposedWithDirectionFilter] = useState("all");
  const [stayOrderDateFilter, setStayOrderDateFilter] = useState(null);
  const [selectedAuthorityDepartments, setSelectedAuthorityDepartments] = useState([]);
  const [expandedFilterSections, setExpandedFilterSections] = useState({});
  const [showAuthorityDeptModal, setShowAuthorityDeptModal] = useState(false);
  const [tempAuthorityDepartments, setTempAuthorityDepartments] = useState([]);

  const toggleFilterSection = (key) =>
    setExpandedFilterSections((prev) => ({ ...prev, [key]: !prev[key] }));

  // Keep the latest callback without re-running the filter effect on identity changes.
  const onFilteredChangeRef = useRef(onFilteredChange);
  useEffect(() => {
    onFilteredChangeRef.current = onFilteredChange;
  }, [onFilteredChange]);

  // Fetch labels for the "Filter By Label" section.
  useEffect(() => {
    const fetchLabels = async () => {
      if (!globalUser?.uid) return;
      try {
        const userLabels = await getUserLabels(globalUser.uid);
        setLabels(userLabels);
      } catch (error) {
        console.error("Error fetching labels:", error);
      }
    };
    fetchLabels();
  }, [globalUser]);

  // Fetch department verification flag (gates the Authority Department filter).
  useEffect(() => {
    const fetchDeptVerification = async () => {
      if (!globalUser?.uid) {
        setIsDepartmentVerified(false);
        return;
      }
      try {
        const userCollectionName =
          localStorage.getItem("userType") === "client" ? "client" : "lawyers";
        const userDoc = await getDoc(doc(db, userCollectionName, globalUser.uid));
        setIsDepartmentVerified(
          userDoc.exists() ? Boolean(userDoc.data().departmentVerified) : false
        );
      } catch (error) {
        console.error("Error fetching department verification:", error);
        setIsDepartmentVerified(false);
      }
    };
    fetchDeptVerification();
  }, [globalUser]);

  // Department filter options: the fixed list plus any free-text departments
  // that actually exist in the user's cases (e.g. Excel-imported values).
  const departmentOptions = useMemo(() => {
    const seen = new Set(AUTHORITY_DEPARTMENTS.map((d) => d.toLowerCase()));
    const extras = [];
    (cases || []).forEach((c) => {
      const dept = String(c.authorityDepartment || "").trim();
      if (!dept) return;
      const key = dept.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      extras.push(dept);
    });
    extras.sort((a, b) => a.localeCompare(b));
    return [...AUTHORITY_DEPARTMENTS, ...extras];
  }, [cases]);

  // Compute the filtered list. Preserves the incoming order (no re-sorting).
  // useLayoutEffect so the parent updates before paint (no empty-list flash).
  useLayoutEffect(() => {
    let filtered = cases || [];

    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (caseItem) => caseItem.status?.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    if (labelFilter !== "all") {
      filtered = filtered.filter(
        (caseItem) => caseItem.labels && caseItem.labels.includes(labelFilter)
      );
    }

    if (hearingDateFilter) {
      const selectedDate = hearingDateFilter; // yyyy-mm-dd from input
      filtered = filtered.filter((caseItem) => {
        if (!caseItem.nextHearingDate) return false;
        const sep = caseItem.nextHearingDate.includes("/") ? "/" : "-";
        const [day, month, year] = caseItem.nextHearingDate.split(sep).map(Number);
        const formattedDate = `${year}-${String(month).padStart(2, "0")}-${String(
          day
        ).padStart(2, "0")}`;
        return formattedDate === selectedDate;
      });
    }

    if (disposedWithDirectionFilter === "yes") {
      filtered = filtered.filter((c) => Boolean(c.disposedWithDirection));
    }

    if (stayOrderDateFilter) {
      const day = String(stayOrderDateFilter.getDate()).padStart(2, "0");
      const month = String(stayOrderDateFilter.getMonth() + 1).padStart(2, "0");
      const year = stayOrderDateFilter.getFullYear();
      const target = `${day}/${month}/${year}`;
      filtered = filtered.filter((c) => c.stayOrderDate === target);
    }

    if (selectedAuthorityDepartments.length > 0) {
      const deptSet = new Set(
        selectedAuthorityDepartments.map((d) => String(d).trim().toLowerCase())
      );
      filtered = filtered.filter((item) =>
        deptSet.has(String(item.authorityDepartment || "").trim().toLowerCase())
      );
    }

    if (searchQuery) {
      const normalizedQuery = searchQuery.toLowerCase();
      const matchesDeptFields = (item) =>
        isDepartmentVerified &&
        ((item.authorityDepartment === "Others"
          ? item.authorityDepartmentOther
          : item.authorityDepartment
        )?.toLowerCase().includes(normalizedQuery) ||
          item.properties?.toLowerCase().includes(normalizedQuery) ||
          item.area?.toLowerCase().includes(normalizedQuery) ||
          item.village?.toLowerCase().includes(normalizedQuery));
      if (searchField === "caseNumber") {
        filtered = filtered.filter(
          (item) =>
            item.caseNumber?.toLowerCase().includes(normalizedQuery) ||
            item.payload?.caseNumber?.toLowerCase().includes(normalizedQuery) ||
            item.payload?.diaryNumber?.toLowerCase().includes(normalizedQuery) ||
            String(item.caseYear || "").toLowerCase().includes(normalizedQuery)
        );
      } else if (searchField === "authorityDepartment") {
        filtered = filtered.filter((item) => {
          const dept =
            item.authorityDepartment === "Others"
              ? item.authorityDepartmentOther
              : item.authorityDepartment;
          return (
            dept?.toLowerCase().includes(normalizedQuery) ||
            item.properties?.toLowerCase().includes(normalizedQuery) ||
            item.area?.toLowerCase().includes(normalizedQuery) ||
            item.village?.toLowerCase().includes(normalizedQuery)
          );
        });
      } else if (searchField === "counterAffidavitStatus") {
        filtered = filtered.filter((item) =>
          item.counterAffidavitStatus?.toLowerCase().includes(normalizedQuery)
        );
      } else if (disposedWithDirectionFilter === "yes") {
        filtered = filtered.filter(
          (item) =>
            item.caseNumber?.toLowerCase().includes(normalizedQuery) ||
            item.payload?.caseNumber?.toLowerCase().includes(normalizedQuery) ||
            item.payload?.diaryNumber?.toLowerCase().includes(normalizedQuery) ||
            item.filingNumber?.toLowerCase().includes(normalizedQuery) ||
            String(item.caseYear || "").toLowerCase().includes(normalizedQuery) ||
            item.type?.toLowerCase().includes(normalizedQuery) ||
            item.petitionerName?.toLowerCase().includes(normalizedQuery) ||
            item.respondentName?.toLowerCase().includes(normalizedQuery) ||
            item.court?.toLowerCase().includes(normalizedQuery) ||
            item.petitionerAdvocate?.toLowerCase().includes(normalizedQuery) ||
            item.respondentAdvocate?.toLowerCase().includes(normalizedQuery) ||
            (Array.isArray(item.allPetitionerAdvocates) &&
              item.allPetitionerAdvocates.some((a) =>
                a?.name?.toLowerCase().includes(normalizedQuery)
              )) ||
            (Array.isArray(item.allRespondentAdvocates) &&
              item.allRespondentAdvocates.some((a) =>
                a?.name?.toLowerCase().includes(normalizedQuery)
              )) ||
            item.directionNotes?.toLowerCase().includes(normalizedQuery) ||
            matchesDeptFields(item)
        );
      } else {
        filtered = filtered.filter(
          (item) =>
            item.caseNumber?.toLowerCase().includes(normalizedQuery) ||
            item.payload?.caseNumber?.toLowerCase().includes(normalizedQuery) ||
            item.payload?.diaryNumber?.toLowerCase().includes(normalizedQuery) ||
            item.filingNumber?.toLowerCase().includes(normalizedQuery) ||
            String(item.caseYear || "").toLowerCase().includes(normalizedQuery) ||
            item.type?.toLowerCase().includes(normalizedQuery) ||
            item.petitionerName?.toLowerCase().includes(normalizedQuery) ||
            item.respondentName?.toLowerCase().includes(normalizedQuery) ||
            item.court?.toLowerCase().includes(normalizedQuery) ||
            item.petitionerAdvocate?.toLowerCase().includes(normalizedQuery) ||
            item.respondentAdvocate?.toLowerCase().includes(normalizedQuery) ||
            (Array.isArray(item.allPetitionerAdvocates) &&
              item.allPetitionerAdvocates.some((a) =>
                a?.name?.toLowerCase().includes(normalizedQuery)
              )) ||
            (Array.isArray(item.allRespondentAdvocates) &&
              item.allRespondentAdvocates.some((a) =>
                a?.name?.toLowerCase().includes(normalizedQuery)
              )) ||
            matchesDeptFields(item)
        );
      }
    }

    if (caseYearFilter) {
      const yearStr = String(caseYearFilter);
      filtered = filtered.filter((item) => {
        const cy = String(item.caseYear || "");
        if (cy) return cy === yearStr;
        const cn = item.caseNumber || "";
        const pcn = item.payload?.caseNumber || "";
        if (!cn && !pcn) return false;
        return cn.includes(yearStr) || pcn.includes(yearStr);
      });
    }

    onFilteredChangeRef.current?.(filtered);
  }, [
    cases,
    searchQuery,
    searchField,
    statusFilter,
    labelFilter,
    hearingDateFilter,
    caseYearFilter,
    disposedWithDirectionFilter,
    stayOrderDateFilter,
    selectedAuthorityDepartments,
    isDepartmentVerified,
  ]);

  let searchPlaceholder = isDepartmentVerified
    ? "Search by case number, year, court, petitioner, respondent, advocate, case type, department, village/division, khasra/plot, area..."
    : "Search by case number, year, filing number, court, petitioner, respondent, petitioner advocate, respondent advocate, case type...";
  if (searchField === "authorityDepartment") {
    searchPlaceholder =
      selectedAuthorityDepartments.length > 0
        ? "Search village, properties, or area in selected departments..."
        : "Type department, properties, area, or village...";
  } else if (searchField === "counterAffidavitStatus") {
    searchPlaceholder = "Type counter affidavit status...";
  } else if (searchField === "caseNumber") {
    searchPlaceholder = "Type case number...";
  }

  return (
    <div className={`relative flex items-center gap-1 ${className}`}>
      <div className="relative flex-1 min-w-0">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 sm:h-5 sm:w-5" />
        <input
          id="search-input"
          type="text"
          placeholder={searchPlaceholder}
          className="w-full py-2 sm:py-2.5 pl-9 sm:pl-10 pr-3 sm:pr-4 bg-gray-800 rounded-full text-sm focus:outline-none focus:ring-1 focus:ring-yellow-500 text-ellipsis overflow-hidden"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <button
        onClick={() => setShowFilterOptions((prev) => !prev)}
        className="flex items-center text-yellow-500 p-2 rounded-full hover:bg-gray-700 transition-colors duration-150 flex-shrink-0"
        title="Filter Cases"
      >
        <Filter className="h-5 w-5" />
      </button>

      {showFilterOptions && (
        <div className="absolute top-full right-0 mt-2 bg-[#1e1b4b] text-gray-300 rounded-lg pt-4 px-4 pb-6 shadow-xl z-30 w-64 border border-gray-700 max-h-[calc(100vh-6rem)] overflow-y-auto overscroll-contain">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-yellow-500">Filter Options</h3>
            <button
              onClick={() => setShowFilterOptions(false)}
              className="text-gray-400 hover:text-white"
            >
              <X size={20} />
            </button>
          </div>

          <div className="mb-3 border-b border-gray-700/50 pb-2">
            <button
              type="button"
              onClick={() => toggleFilterSection("hearingDate")}
              className="w-full flex justify-between items-center text-sm font-medium text-gray-400 mb-2 hover:text-gray-200"
            >
              <span className="flex items-center gap-1">
                <Calendar size={14} />
                Next Hearing Date
              </span>
              <ChevronDown
                size={16}
                className={`transition-transform ${
                  expandedFilterSections.hearingDate ? "rotate-180" : ""
                }`}
              />
            </button>
            {expandedFilterSections.hearingDate && (
              <div className="flex flex-col space-y-1">
                <input
                  type="date"
                  value={hearingDateFilter}
                  onChange={(e) => setHearingDateFilter(e.target.value)}
                  className="w-full p-2 rounded text-sm bg-gray-800 text-gray-300 border border-gray-600 focus:outline-none focus:ring-1 focus:ring-yellow-500 [color-scheme:dark]"
                />
                {hearingDateFilter && (
                  <button
                    className="w-full text-left p-2 rounded text-sm text-red-400 hover:bg-[#2d2a5a]"
                    onClick={() => setHearingDateFilter("")}
                  >
                    Clear Date Filter
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="mb-3 border-b border-gray-700/50 pb-2">
            <button
              type="button"
              onClick={() => toggleFilterSection("caseYear")}
              className="w-full flex justify-between items-center text-sm font-medium text-gray-400 mb-2 hover:text-gray-200"
            >
              <span className="flex items-center gap-1">
                <Calendar size={14} />
                Case Year
              </span>
              <ChevronDown
                size={16}
                className={`transition-transform ${
                  expandedFilterSections.caseYear ? "rotate-180" : ""
                }`}
              />
            </button>
            {expandedFilterSections.caseYear && (
              <div className="flex flex-col space-y-1">
                <DatePicker
                  selected={caseYearFilter ? new Date(caseYearFilter, 0, 1) : null}
                  onChange={(date) => {
                    const year = date ? date.getFullYear().toString() : "";
                    setCaseYearFilter(year);
                  }}
                  showYearPicker
                  dateFormat="yyyy"
                  placeholderText="Select case year"
                  yearItemNumber={9}
                  className="w-full p-2 rounded text-sm bg-gray-800 text-gray-300 border border-gray-600 focus:outline-none focus:ring-1 focus:ring-yellow-500"
                  popperPlacement="bottom-start"
                />
                {caseYearFilter && (
                  <button
                    className="w-full text-left p-2 rounded text-sm text-red-400 hover:bg-[#2d2a5a]"
                    onClick={() => setCaseYearFilter("")}
                  >
                    Clear Year Filter
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="mb-3 border-b border-gray-700/50 pb-2">
            <button
              type="button"
              onClick={() => toggleFilterSection("status")}
              className="w-full flex justify-between items-center text-sm font-medium text-gray-400 mb-2 hover:text-gray-200"
            >
              <span>Filter By Status</span>
              <ChevronDown
                size={16}
                className={`transition-transform ${
                  expandedFilterSections.status ? "rotate-180" : ""
                }`}
              />
            </button>
            {expandedFilterSections.status && (
              <div className="flex flex-col space-y-1">
                {["all", "pending", "disposed"].map((status) => (
                  <button
                    key={status}
                    className={`w-full text-left p-2 rounded text-sm capitalize ${
                      statusFilter === status.toLowerCase()
                        ? "bg-gray-600 text-white"
                        : "text-gray-300 hover:bg-[#2d2a5a]"
                    }`}
                    onClick={() => {
                      setStatusFilter(status.toLowerCase());
                      setShowFilterOptions(false);
                    }}
                  >
                    {status}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="mb-3 border-b border-gray-700/50 pb-2">
            <button
              type="button"
              onClick={() => toggleFilterSection("label")}
              className="w-full flex justify-between items-center text-sm font-medium text-gray-400 mb-2 hover:text-gray-200"
            >
              <span>Filter By Label</span>
              <ChevronDown
                size={16}
                className={`transition-transform ${
                  expandedFilterSections.label ? "rotate-180" : ""
                }`}
              />
            </button>
            {expandedFilterSections.label && (
              <div className="flex flex-col space-y-1">
                {labelFilter !== "all" && (
                  <button
                    className="w-full text-left p-2 rounded text-sm text-red-400 hover:bg-[#2d2a5a]"
                    onClick={() => {
                      setLabelFilter("all");
                      setShowFilterOptions(false);
                    }}
                  >
                    Clear Label Filter
                  </button>
                )}
                {labels.map((label) => (
                  <button
                    key={label.id}
                    className={`w-full text-left p-2 rounded text-sm flex items-center gap-2 ${
                      labelFilter === label.id
                        ? "bg-gray-600 text-white"
                        : "text-gray-300 hover:bg-[#2d2a5a]"
                    }`}
                    onClick={() => {
                      setLabelFilter(label.id);
                      setShowFilterOptions(false);
                    }}
                  >
                    <div
                      className="w-3 h-3 rounded flex-shrink-0"
                      style={{ backgroundColor: label.color }}
                    />
                    {label.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="mb-3 border-b border-gray-700/50 pb-2">
            <button
              type="button"
              onClick={() => toggleFilterSection("stayOrder")}
              className="w-full flex justify-between items-center text-sm font-medium text-gray-400 mb-2 hover:text-gray-200"
            >
              <span className="flex items-center gap-1">
                <Calendar size={14} />
                Stay Order Date
              </span>
              <ChevronDown
                size={16}
                className={`transition-transform ${
                  expandedFilterSections.stayOrder ? "rotate-180" : ""
                }`}
              />
            </button>
            {expandedFilterSections.stayOrder && (
              <div className="flex flex-col space-y-1">
                <DatePicker
                  selected={stayOrderDateFilter}
                  onChange={(date) => setStayOrderDateFilter(date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Select stay order date"
                  className="w-full p-2 rounded text-sm bg-gray-800 text-gray-300 border border-gray-600 focus:outline-none focus:ring-1 focus:ring-yellow-500"
                  popperPlacement="bottom-start"
                />
                {stayOrderDateFilter && (
                  <button
                    className="w-full text-left p-2 rounded text-sm text-red-400 hover:bg-[#2d2a5a]"
                    onClick={() => setStayOrderDateFilter(null)}
                  >
                    Clear Stay Order Date
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="mb-3">
            <button
              type="button"
              onClick={() => toggleFilterSection("more")}
              className="w-full flex justify-between items-center text-sm font-medium text-gray-400 mb-2 hover:text-gray-200"
            >
              <span>More Filters</span>
              <ChevronDown
                size={16}
                className={`transition-transform ${
                  expandedFilterSections.more ? "rotate-180" : ""
                }`}
              />
            </button>
            {expandedFilterSections.more && (
              <div className="flex flex-col space-y-1">
                <button
                  className={`w-full text-left p-2 rounded text-sm ${
                    disposedWithDirectionFilter === "yes"
                      ? "bg-gray-600 text-white"
                      : "text-gray-300 hover:bg-[#2d2a5a]"
                  }`}
                  onClick={() => {
                    const turningOn = disposedWithDirectionFilter !== "yes";
                    setDisposedWithDirectionFilter(turningOn ? "yes" : "all");
                    if (turningOn) setSearchField("all");
                    setShowFilterOptions(false);
                  }}
                >
                  Disposed with Direction
                </button>
                {isDepartmentVerified && (
                  <button
                    className={`w-full text-left p-2 rounded text-sm flex items-center justify-between ${
                      searchField === "authorityDepartment" ||
                      selectedAuthorityDepartments.length > 0
                        ? "bg-gray-600 text-white"
                        : "text-gray-300 hover:bg-[#2d2a5a]"
                    }`}
                    onClick={() => {
                      setTempAuthorityDepartments([...selectedAuthorityDepartments]);
                      setShowAuthorityDeptModal(true);
                      setShowFilterOptions(false);
                    }}
                  >
                    <span>Authority Department</span>
                    {selectedAuthorityDepartments.length > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center text-[10px] font-semibold bg-yellow-500 text-black rounded-full px-2 py-0.5">
                        {selectedAuthorityDepartments.length}
                      </span>
                    )}
                  </button>
                )}
                <button
                  className={`w-full text-left p-2 rounded text-sm ${
                    searchField === "counterAffidavitStatus"
                      ? "bg-gray-600 text-white"
                      : "text-gray-300 hover:bg-[#2d2a5a]"
                  }`}
                  onClick={() => {
                    const turningOn = searchField !== "counterAffidavitStatus";
                    setSearchField(turningOn ? "counterAffidavitStatus" : "all");
                    if (turningOn) setDisposedWithDirectionFilter("all");
                    setShowFilterOptions(false);
                  }}
                >
                  Status of Counter Affidavit
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Authority Department Multi-Select Modal */}
      {showAuthorityDeptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[1200] p-4">
          <div className="bg-[#0B0A29] text-white rounded-lg shadow-xl w-full max-w-md border border-gray-700 flex flex-col max-h-[85vh]">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-yellow-500">Filter by Department</h3>
              <button
                onClick={() => setShowAuthorityDeptModal(false)}
                className="text-gray-400 hover:text-white"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="px-6 py-3 border-b border-gray-700/70">
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-yellow-500"
                  checked={tempAuthorityDepartments.length === departmentOptions.length}
                  onChange={(e) => {
                    setTempAuthorityDepartments(
                      e.target.checked ? [...departmentOptions] : []
                    );
                  }}
                />
                <span className="font-medium">Select All</span>
                <span className="ml-auto text-xs text-gray-400">
                  {tempAuthorityDepartments.length}/{departmentOptions.length} selected
                </span>
              </label>
            </div>

            <div className="px-6 py-3 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {departmentOptions.map((dept) => {
                  const checked = tempAuthorityDepartments.includes(dept);
                  return (
                    <label
                      key={dept}
                      className="flex items-center gap-2 px-3 py-2 rounded cursor-pointer hover:bg-[#1e1b4b] text-sm"
                    >
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-yellow-500"
                        checked={checked}
                        onChange={(e) => {
                          setTempAuthorityDepartments((prev) =>
                            e.target.checked
                              ? [...prev, dept]
                              : prev.filter((d) => d !== dept)
                          );
                        }}
                      />
                      <span>{dept}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-between items-center px-6 py-4 border-t border-gray-700">
              <button
                onClick={() => setTempAuthorityDepartments([])}
                className="text-sm text-red-400 hover:text-red-300"
              >
                Clear
              </button>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowAuthorityDeptModal(false)}
                  className="px-4 py-2 rounded text-sm font-medium bg-white text-black hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setSelectedAuthorityDepartments([...tempAuthorityDepartments]);
                    const turningOn = tempAuthorityDepartments.length > 0;
                    setSearchField(turningOn ? "authorityDepartment" : "all");
                    if (turningOn) setDisposedWithDirectionFilter("all");
                    setShowAuthorityDeptModal(false);
                  }}
                  className="px-4 py-2 rounded text-sm font-medium bg-yellow-500 text-black hover:bg-yellow-400 transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
